import { Viewer } from "@photo-sphere-viewer/core";
import { AutorotatePlugin } from "@photo-sphere-viewer/autorotate-plugin";
import { GalleryPlugin } from "@photo-sphere-viewer/gallery-plugin";
import { CompassPlugin } from "@photo-sphere-viewer/compass-plugin";
import { GyroscopePlugin } from "@photo-sphere-viewer/gyroscope-plugin";
import { MarkersPlugin } from "@photo-sphere-viewer/markers-plugin";
import { VirtualTourPlugin } from "@photo-sphere-viewer/virtual-tour-plugin";
import { VideoPlugin } from "@photo-sphere-viewer/video-plugin";
import { VisibleRangePlugin } from "@photo-sphere-viewer/visible-range-plugin";
import { SettingsPlugin } from "@photo-sphere-viewer/settings-plugin";
import { EquirectangularVideoAdapter } from "@photo-sphere-viewer/equirectangular-video-adapter";

import "@photo-sphere-viewer/core/index.css";
import "@photo-sphere-viewer/gallery-plugin/index.css";
import "@photo-sphere-viewer/compass-plugin/index.css";
import "@photo-sphere-viewer/markers-plugin/index.css";
import "@photo-sphere-viewer/virtual-tour-plugin/index.css";
import "@photo-sphere-viewer/video-plugin/index.css";
import "@photo-sphere-viewer/settings-plugin/index.css";
import "./style.css";

/**
 * Start age-gate script
 */

// Shared helper: initialise a single viewer container from its data-settings
function initViewerElement(el) {
  // Destroy any previous PSV instance (handles Elementor editor re-renders)
  if (el.dataset.psvInitialized && el.__psvViewer) {
    try {
      el.__psvViewer.destroy();
    } catch (e) {
      /* noop */
    }
    // Cleared so the guard in the frame callback below builds a new viewer
    // instead of keeping the destroyed one.
    el.__psvViewer = null;
  }
  el.dataset.psvInitialized = "1";

  var settings =
    typeof jQuery !== "undefined"
      ? jQuery(el).data("settings")
      : JSON.parse(el.getAttribute("data-settings") || "{}");

  if (!settings) return;

  var navbar = sortNavbar(settings.navbar);

  // GyroscopePlugin registers its button only into PSV's *default* navbar, and
  // every viewer here passes an explicit list — so the button never appeared
  // and the gyroscope could not be switched on. Add it whenever the plugin is.
  if (
    settings.gyroscopePlugin &&
    Array.isArray(navbar) &&
    navbar.indexOf("gyroscope") === -1
  ) {
    navbar = sortNavbar(navbar.concat("gyroscope"));
  }

  var navbarStr = Array.isArray(navbar)
    ? navbar.join(",")
    : String(navbar || "");

  // Build plugins array
  var plugins = buildPlugins(settings, navbarStr);

  // Build viewer config – always pass the actual DOM element as container
  var config = {
    ...settings,
    container: el,
    navbar: navbar,
    plugins: plugins,
  };

  // If this is a video viewer, swap in the video adapter
  // autoplay and muted are adapter options in Photo Sphere Viewer 5. They
  // used to go to VideoPlugin, which logged "Unknown option" and ignored
  // them, so neither setting did anything.
  var videoOptions = settings.videoPlugin || {};
  if (settings.isVideo && settings.videoSource) {
    config.adapter = EquirectangularVideoAdapter.withConfig({
      autoplay: !!videoOptions.autoplay,
      muted: !!videoOptions.muted,
    });
    config.panorama = { source: settings.videoSource };
  }

  // Remove our custom keys so PSV doesn't choke on them
  delete config.galleryPlugin;
  delete config.hotspotsPlugin;
  delete config.compassPlugin;
  delete config.gyroscopePlugin;
  delete config.visibleRangePlugin;
  delete config.virtualTourPlugin;
  delete config.videoPlugin;
  delete config.videoSource;
  delete config.isVideo;
  delete config.autorotateAutoplay;
  // Autorotate plugin options, applied in buildPlugins().
  delete config.autostartDelay;
  delete config.autorotateSpeed;
  // Photo Sphere Viewer 4 options that version 5 dropped. Older saved
  // widgets and shortcodes still carry them; PSV would log a warning each.
  delete config.longitudeRange;
  delete config.latitudeRange;
  delete config.captureCursor;
  delete config.mousewheelSpeed;
  delete config.zoomButtonIncrement;

  // Map legacy v4 keys to PSV v5 keys
  if (config.defaultLong !== undefined) {
    config.defaultYaw = config.defaultLong + "deg";
    delete config.defaultLong;
  }
  if (config.defaultLat !== undefined) {
    config.defaultPitch = config.defaultLat + "deg";
    delete config.defaultLat;
  }

  // Defer creation to next frame so the browser has finished laying out the
  // container (fixes 0-dimension containers in Elementor columns).
  requestAnimationFrame(function () {
    if (el.__psvViewer) return; // another init beat us here
    try {
      el.__psvViewer = new Viewer(config);
      if (settings.isVideo) {
        // The adapter always creates a looping <video>; honour "Loop" off.
        el.__psvViewer.addEventListener("panorama-loaded", function (e) {
          var video = e.data && e.data.texture && e.data.texture.image;
          if (video instanceof HTMLVideoElement) {
            video.loop = !!videoOptions.loop;
          }
        });
      }
    } catch (e) {
      // A bad option or a browser without WebGL throws here; say so instead
      // of leaving an empty box on the page.
      console.error("[Photo Sphere Viewer]", e);
      var msg = document.createElement("div");
      msg.className = "psv-init-error";
      msg.textContent = "This 360° viewer could not be loaded in your browser.";
      el.appendChild(msg);
    }
  });
}

// Canonical left-to-right navbar order, matching the order the Pro add-on
// applies server-side, so a shortcode and a widget look the same.
var NAVBAR_ORDER = [
  "videoPlay",
  "videoTime",
  "zoom",
  "move",
  "autorotate",
  "caption",
  "gyroscope",
  "download",
  "description",
  "fullscreen",
  "gallery",
  "videoVolume",
];

/**
 * Normalise `navbar` from data-settings into an ordered array, or `false`
 * when the navbar is switched off.
 */
function sortNavbar(navbar) {
  if (
    navbar === false ||
    navbar === undefined ||
    navbar === null ||
    navbar === ""
  ) {
    return false;
  }

  var items = (Array.isArray(navbar) ? navbar : String(navbar).split(","))
    .map(function (item) {
      return typeof item === "string" ? item.trim() : item;
    })
    .filter(Boolean);

  if (!items.length) return false;

  return items.sort(function (a, b) {
    var ai = NAVBAR_ORDER.indexOf(a);
    var bi = NAVBAR_ORDER.indexOf(b);
    // Unknown items keep their relative position at the end.
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

/**
 * Build the PSV plugins array from data-settings.
 */
function buildPlugins(settings, navbarStr) {
  var plugins = [];

  // Autorotate
  if (navbarStr.includes("autorotate")) {
    // Only auto-start rotation when the autoplay flag is explicitly enabled.
    // When disabled (or absent, e.g. legacy data without the flag), the
    // navbar button is still shown but rotation must be started manually.
    var shouldAutoplay = settings.autorotateAutoplay === true;
    var delay = Number(settings.autostartDelay);
    var autorotateConfig = {
      autostartDelay: shouldAutoplay
        ? isFinite(delay) && settings.autostartDelay !== undefined
          ? delay
          : 1000
        : Number.POSITIVE_INFINITY,
      autostartOnIdle: shouldAutoplay,
      autorotatePitch: "5deg",
    };
    // Radians per second; unset keeps the plugin's own 2 rpm.
    if (typeof settings.autorotateSpeed === "number") {
      autorotateConfig.autorotateSpeed = settings.autorotateSpeed;
    }
    plugins.push(AutorotatePlugin.withConfig(autorotateConfig));
  }

  // Gallery
  var gc = settings.galleryPlugin;
  if (gc && gc.items && gc.items.length) {
    plugins.push(
      GalleryPlugin.withConfig({
        items: gc.items,
        visibleOnLoad: !!gc.visibleOnLoad,
        hideOnClick: !!gc.hideOnClick,
        navigationArrows: !!gc.navigationArrows,
        thumbnailSize: gc.thumbnailSize || { width: 200, height: 100 },
      }),
    );
  }

  // Compass (premium)
  var cc = settings.compassPlugin;
  if (cc) {
    plugins.push(
      CompassPlugin.withConfig({
        size: cc.size || "120px",
        navigation: true,
      }),
    );
  }

  // Gyroscope
  if (settings.gyroscopePlugin) {
    plugins.push(GyroscopePlugin);
  }

  // Visible Range (premium)
  var vr = settings.visibleRangePlugin;
  if (vr) {
    plugins.push(
      VisibleRangePlugin.withConfig({
        horizontalRange: vr.horizontalRange || null,
        verticalRange: vr.verticalRange || null,
      }),
    );
  }

  // Info hotspots (free up to a limit, enforced in PHP)
  var markersRegistered = false;
  var hs = settings.hotspotsPlugin;
  if (hs && hs.markers && hs.markers.length) {
    plugins.push(MarkersPlugin.withConfig({ markers: hs.markers }));
    markersRegistered = true;
  }

  // Virtual Tour (premium)
  var vt = settings.virtualTourPlugin;
  if (vt && vt.nodes && vt.nodes.length) {
    // Markers plugin is required when renderMode is 'markers'
    if (vt.renderMode === "markers" && !markersRegistered) {
      plugins.push(MarkersPlugin);
    }
    plugins.push(
      VirtualTourPlugin.withConfig({
        nodes: vt.nodes,
        startNodeId: vt.startNodeId || vt.nodes[0].id,
        renderMode: vt.renderMode || "3d",
      }),
    );
  }

  // Video
  var vp = settings.videoPlugin;
  if (vp) {
    plugins.push(SettingsPlugin);
    // autoplay, muted and loop are applied to the adapter and the <video>
    // element in initViewerElement(); VideoPlugin has no such options.
    plugins.push(VideoPlugin);
  }

  return plugins;
}

if (typeof jQuery !== "undefined") {
  (function ($) {
    "use strict";

    var PhotoSphereViewer = function ($scope, $) {
      var $container = $scope.find(".photo-sphere-viewer");

      if (!$container.length) {
        return;
      }

      $.each($container, function () {
        initViewerElement(this);
      });
    };

    jQuery(window).on("elementor/frontend/init", function () {
      var elementorFrontend = window.elementorFrontend;
      if (elementorFrontend && elementorFrontend.hooks) {
        elementorFrontend.hooks.addAction(
          "frontend/element_ready/photo-sphere-viewer.default",
          PhotoSphereViewer,
        );
        elementorFrontend.hooks.addAction(
          "frontend/element_ready/psv-virtual-tour.default",
          PhotoSphereViewer,
        );
        elementorFrontend.hooks.addAction(
          "frontend/element_ready/psv-video-360.default",
          PhotoSphereViewer,
        );
      }
    });

    // Fallback: initialise any Elementor widget viewers that were NOT handled
    // by Elementor's frontend handler (only runs on non-Elementor pages).
    jQuery(function () {
      if (window.elementorFrontend && window.elementorFrontend.hooks) return;
      document
        .querySelectorAll(".photo-sphere-viewer[data-settings]")
        .forEach(function (el) {
          if (!el.dataset.psvInitialized) {
            initViewerElement(el);
          }
        });
    });
  })(jQuery);
}

/**
 * End age-gate script
 */

// Shortcodes render into `.xero-psv-container`. They go through the same
// initialiser as the Elementor widgets — the shortcode path used to be a
// second, drifted copy that never attached the video adapter, so [psv_video]
// produced a viewer with no source at all.
function initShortcodeViewers(root) {
  (root || document)
    .querySelectorAll(".xero-psv-container")
    .forEach(function (wrapper) {
      var el = wrapper.firstElementChild;
      if (!el || el.dataset.psvInitialized) return;
      initViewerElement(el);
    });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", function () {
    initShortcodeViewers();
  });
} else {
  initShortcodeViewers();
}

// The admin app renders a live demo viewer after React mounts, which is long
// after the pass above has run. Expose the initialiser so it can ask for a
// second pass over a subtree it just created.
window.psvInitViewers = initShortcodeViewers;

/** Radians from the viewer to degrees in the ranges the editors use. */
function toDegrees(radians, isYaw) {
  var deg = Math.round((radians * 180) / Math.PI);
  if (isYaw && deg > 180) deg -= 360;
  return deg;
}

/**
 * Position picker for the block editor.
 *
 * Shows a panorama in `el` and reports where the user clicks, in degrees, so
 * hotspots and tour arrows are placed by pointing at the photo instead of
 * guessing yaw values. The editor loads this bundle on demand (see
 * src/shared/PositionPicker.js) rather than shipping three.js itself.
 *
 * @param {HTMLElement} el
 * @param {string}      panorama Image URL.
 * @param {Object}      options  { markers: [{id, yaw, pitch, label}], onPick({yaw, pitch}) }
 * @return {{getView: Function, destroy: Function}}
 */
window.psvPositionPicker = function (el, panorama, options) {
  options = options || {};

  var toMarker = function (m) {
    return {
      id: String(m.id),
      position: { yaw: m.yaw + "deg", pitch: m.pitch + "deg" },
      html:
        '<span class="psv-hotspot' +
        (m.current ? "" : " psv-hotspot--dark") +
        '"></span>',
      size: { width: 22, height: 22 },
      anchor: "center center",
      tooltip: m.label
        ? { content: String(m.label), position: "top center" }
        : undefined,
    };
  };

  var viewer = new Viewer({
    container: el,
    panorama: panorama,
    navbar: ["zoom", "fullscreen"],
    defaultYaw: (options.yaw || 0) + "deg",
    defaultPitch: (options.pitch || 0) + "deg",
    plugins: [
      MarkersPlugin.withConfig({
        markers: (options.markers || []).map(toMarker),
      }),
    ],
  });

  var markers = viewer.getPlugin(MarkersPlugin);

  viewer.addEventListener("click", function (e) {
    if (e.data.rightclick) return;
    var pick = {
      yaw: toDegrees(e.data.yaw, true),
      pitch: toDegrees(e.data.pitch, false),
    };
    var marker = toMarker({
      id: "psv-picker-current",
      current: true,
      yaw: pick.yaw,
      pitch: pick.pitch,
    });
    if (
      markers.getMarkers().some(function (m) {
        return m.id === marker.id;
      })
    ) {
      markers.updateMarker(marker);
    } else {
      markers.addMarker(marker);
    }
    if (options.onPick) options.onPick(pick);
  });

  return {
    getView: function () {
      var p = viewer.getPosition();
      return { yaw: toDegrees(p.yaw, true), pitch: toDegrees(p.pitch, false) };
    },
    destroy: function () {
      viewer.destroy();
    },
  };
};
